import { getConnectors } from '@wagmi/core'
import { Dialog, Remote, Storage, TrustedHosts } from 'accounts'
import { http } from 'viem'
import { type Capabilities, http as zone_http } from 'viem/tempo'
import { createConfig } from 'wagmi'
import { tempo, tempoDevnet, tempoLocalnet, tempoModerato } from 'wagmi/chains'
import { tempoWallet, webAuthn } from 'wagmi/tempo'

import { localTempoEnabled, shouldDefaultToTestnet } from './chain.js'
import * as Messenger from './messenger.js'
import { chain as zone, rpcUrl as zoneRpcUrl } from './zone.js'

const webAuthnStorage = Storage.combine(Storage.cookie(), Storage.localStorage())
const walletStorage = Storage.combine(
  Storage.cookie({ key: 'tempo-wallet' }),
  Storage.localStorage({ key: 'tempo-wallet' }),
)

/** Provider instance for executing confirmed requests. */
export const wagmiConfig = createConfig({
  chains: (() => {
    const chains = [tempo, tempoModerato, tempoDevnet, zone] as const
    if (localTempoEnabled) return [tempoLocalnet, ...chains] as const
    return chains
  })(),
  connectors: [
    webAuthn({
      authUrl: '/api/webauthn',
      testnet: import.meta.env.DEV,
      persistCredentials: false,
      maxAccounts: 5,
      mpp: { polyfill: false },
      storage: webAuthnStorage,
    }),
    tempoWallet({
      dialog: Dialog.iframe(),
      host: `${typeof window !== 'undefined' ? window.location.origin : 'https://wallet.tempo.xyz'}/remote`,
      storage: walletStorage,
    }),
  ],
  multiInjectedProviderDiscovery: false,
  transports: {
    [tempo.id]: http(`/api/relay/${tempo.id}`),
    [tempoDevnet.id]: http(`/api/relay/${tempoDevnet.id}`),
    [tempoLocalnet.id]: http(`/api/relay/${tempoLocalnet.id}`),
    [tempoModerato.id]: http(`/api/relay/${tempoModerato.id}`),
    [zone.id]: zone_http(zoneRpcUrl),
  },
})

// Sync wagmi's chain before React mounts so provider calls start on the URL/default chain.
if (typeof window !== 'undefined') {
  const testnet = new URLSearchParams(window.location.search).get('testnet')
  if (testnet === 'true' || (testnet == null && shouldDefaultToTestnet()))
    wagmiConfig.setState((state) => ({ ...state, chainId: tempoModerato.id }))
}

/** Remote context singleton. */
export const remote = Remote.create({
  messenger: Messenger.init(),
  provider: await getConnectors(wagmiConfig as any)[0]!.getProvider(),
  trustedHosts: TrustedHosts.hosts['tempo.xyz'],
})

declare module 'wagmi' {
  interface Register {
    config: typeof wagmiConfig
  }
}

declare module 'viem' {
  interface Register {
    CapabilitiesSchema: Capabilities.Schema
  }
}
